import { CBadge } from "@coreui/react";
import moment from "moment";
import { apiUrl } from "src/constant";

export const customStyles = {
    option: (provided, state) => ({
        ...provided,
        borderBottom: '1px dotted gray',
        color: state.isSelected ? 'white' : 'black',
    }),
    singleValue: (provided, state) => {
        const opacity = state.isDisabled ? 0.5 : 1;
        const transition = 'opacity 300ms';
        return { ...provided, opacity, transition };
    }
};

// react-select renders real DOM/CSS via emotion (not canvas, unlike the
// dashboard's chart colors) -- CSS custom properties work directly in its
// style-function return values, the browser resolves var(--app-*) through
// the normal cascade exactly like any hand-written CSS, so this needs no
// JS-side light/dark resolution or re-render-on-theme-change wiring at all.
// It's a single static object, not a factory -- it just works everywhere
// the app's theme is in effect.
export const themedSelectStyles = {
    control: (base, state) => ({
        ...base,
        backgroundColor: 'var(--app-surface)',
        borderColor: state.isFocused ? 'var(--app-petrol)' : 'var(--app-border)',
        boxShadow: state.isFocused ? '0 0 0 1px var(--app-petrol)' : 'none',
        '&:hover': { borderColor: 'var(--app-petrol)' },
    }),
    menu: (base) => ({
        ...base,
        backgroundColor: 'var(--app-surface)',
        border: '1px solid var(--app-border)',
        zIndex: 20,
    }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected
            ? 'var(--app-petrol)'
            : state.isFocused
            ? 'var(--app-surface-soft)'
            : 'transparent',
        color: state.isSelected ? '#fff' : 'var(--app-text)',
        cursor: 'pointer',
    }),
    singleValue: (base) => ({ ...base, color: 'var(--app-text)' }),
    input: (base) => ({ ...base, color: 'var(--app-text)' }),
    placeholder: (base) => ({ ...base, color: 'var(--app-text-faint)' }),
    multiValue: (base) => ({ ...base, backgroundColor: 'var(--app-surface-soft)' }),
    multiValueLabel: (base) => ({ ...base, color: 'var(--app-text)' }),
    indicatorSeparator: (base) => ({ ...base, backgroundColor: 'var(--app-border)' }),
    dropdownIndicator: (base) => ({ ...base, color: 'var(--app-text-soft)' }),
    clearIndicator: (base) => ({ ...base, color: 'var(--app-text-soft)' }),
};

// Layers a file's own non-color style overrides (border-radius, fontSize,
// layout, etc.) on top of themedSelectStyles, part by part, so neither set
// of rules clobbers the other. Used by the handful of files that already
// had bespoke react-select styling worth preserving.
export const mergeSelectStyles = (overrides = {}) => {
    const merged = { ...themedSelectStyles };
    Object.keys(overrides).forEach((key) => {
        const base = themedSelectStyles[key];
        const extra = overrides[key];
        merged[key] = (provided, state) => ({
            ...(base ? base(provided, state) : provided),
            ...(extra ? extra(provided, state) : {}),
        });
    });
    return merged;
};

export const candidateType = [
    { value: "Fresh", label: "Fresh", field: "phase" },
    { value: "Experience", label: "Experience", field: "phase" },
    { value: "Expert", label: "Expert", field: "phase" },
  ];

export const ticketStatusColors = {
    "In Review": "#3399FF",
    "In Progress": "#E59F14",
    "Resolved": "green",
    "Declined": "#E55353",
};

// Ticket status -> StatusPill tone. Same statuses/semantics as
// ticketStatusColors above, which stays as-is (still used directly where a
// literal hex is actually needed) -- this is the StatusPill-facing sibling,
// shared by the 3 files that each reimplemented a status-colored cell
// (helpdesk/adminView.js, helpdesk/index.js, helpdesk/selfAssignTickets.js).
export const ticketStatusTone = (status) => {
    switch (status) {
        case "In Review": return "info";
        case "In Progress": return "warning";
        case "Resolved": return "success";
        case "Declined": return "danger";
        default: return "neutral";
    }
};

export const ticketColumn = [
    {
        label: "#",
        field: "ticket_no",
        width: 200,
    },
    {
        label: "Priority",
        field: "priority",
        width: 100,
    },
    {
        label: "Title",
        field: "title",
        width: 200,
    },
    {
        label: "Assigned To",
        field: "assigned",
        width: 120,
      },
    {
        label: "Category",
        field: "category",
        width: 200,
    },
    {
        label: "Date",
        field: "date",
        width: 100,
    },
    {
        label: "Status",
        field: "status",
        width: 200,
    },
    {
        label: "Actions",
        field: "action",
        width: 200,
    },
];

export const getPublicHolidays = (holidays, year) => {
    const publicLeaves = [];
    holidays && holidays.filter(item => {
        item.publicHolidays.map(ph => {
            // if (!year || (year && moment(ph).format("YYYY") == year)) {
                publicLeaves.push(moment(ph).format("YYYY-MM-DD"));
            // }
        })
    })
    return publicLeaves;
}



export const getPublicYearlyHolidays = (holidays, year) => {
    const publicLeaves = [];
    holidays && holidays.filter(item => {
        item.publicHolidays.map(ph => {
            if (!year || (year && moment(ph).format("YYYY") == year)) {
                publicLeaves.push(moment(ph).format("YYYY-MM-DD"));
            }
        })
    })
    return publicLeaves;
}


export const getToken=() =>{return  JSON.parse(window.localStorage.getItem('currentUser')) || null};

// A backend-hosted upload endpoint (candidate resumes, user/employee
// pictures) returns a relative path like "/assets/uploads/resumes/x.pdf" --
// same prefixing convention already used for profile pictures
// (ProfilePictureTab.js): `${apiUrl}/api/v1${path}`. Already-absolute
// URLs (e.g. a legacy Filestack/Google-Drive link on an older record)
// pass through untouched.
export const resolveUploadedAssetUrl = (path) => {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  return `${apiUrl}/api/v1${path}`;
};

// The browser's own IANA timezone (e.g. "Asia/Karachi", "America/New_York")
// -- for any backend endpoint whose "today"/"upcoming" cutoff depends on
// the viewer's calendar date, not a fixed company timezone (user
// feedback, 2026-11-02: the app is used from outside Pakistan too, so a
// server-side hardcoded zone is wrong for anyone not in it). Sent as
// `?tz=` on those calls; the backend falls back to its own default only
// if this ever comes back empty (a browser old enough to lack Intl, or a
// non-browser caller).
export const getBrowserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    return "";
  }
};
export const LeavesColors = {
    "Short": "#F9B115",
    "Half Day": "blue",
    "Full Day": "green",
    "Applied": "#4F5D73"
}

// Score -> StatusPill tone, for the 5-bucket evaluation-score coloring that
// was previously reimplemented inline (same thresholds, as a literal
// <div style={{color}}> ladder) in 3 separate files: employeeEvaluation/
// Index.js, employeeEvaluation/employeesEvaluation.js, and Teams/ViewTeam/
// ViewTeam.js. (hrEvaluationDetails.js/leadEvaluationDetails.js render a
// structurally different widget -- a <CButton> text label + separate score
// badge, not a bare colored percentage -- so they're not a 4th duplicate of
// this exact pattern and intentionally don't use this helper.) Thresholds
// unchanged from the original (>=85/70/55/35); the >=70 bucket's color
// shifts from the original literal indigo (#321FDC) to the "info" tone (the
// app's petrol accent) since StatusPill's fixed 5-tone palette has no
// dedicated blue slot -- same semantic "good" meaning, now on the app's own
// established accent instead of a one-off hex.
export const scoreTone = (score) => {
    const n = score ?? 0;
    if (n >= 85) return "success";
    if (n >= 70) return "info";
    if (n >= 55) return "warning";
    if (n >= 35) return "neutral";
    return "danger";
}

export const levaesMapAttendance = (leaves, attendance) => {
    var employeeOnLeave=false;
    var type='';
        leaves.map(row => {
          if (row.ApplicationStatus == "Approved") {
            row.LeavesDates.filter((r) => {
              if (!attendance.checkin && !attendance.checkout) {
                if (moment(r).format("DD MM YYYY") == moment(attendance.date).format("DD MM YYYY")) {
                  employeeOnLeave=true;
                  type=row.LeaveType
                }
              }
            })
          }
    
        })
    return [employeeOnLeave,type];
    
      }

      export const levaesAttendanceMapAttendance = (leaves, attendance) => {
        let employeeOnLeave = false;
        let type = '';

        leaves.forEach(row => {
            if (row.ApplicationStatus === "Approved") {
            row.LeavesDates.forEach(r => {
                if (moment(r).format("DD MM YYYY") === moment(attendance.date).format("DD MM YYYY")) {
                employeeOnLeave = true;
                type = row.LeaveType;
                }
            });
            }
        });

        return [employeeOnLeave, type];
  };

export const AvailedLeavesCalculations = (leaves) => {
    var allowedLeaves = 0;
    leaves.map(leave => {
        if (leave.category != "Unpaid Leaves" && leave.ApplicationStatus != "Applied") {
            if (leave.type == "Full Day") {
                allowedLeaves += leave.Leaves.length;
            }
            if (leave.type == "Half Day") {
                const half = leave.Leaves.length / 2
                allowedLeaves += half;
            }
            if (leave.type == "Short") {
                const short = leave.Leaves.length / 4;
                allowedLeaves += short;
            }
        }
    })
    return allowedLeaves;
}

export const allowedLeaves = (leaveTypes) => {
    const personalLeaves = leaveTypes?.personal_leaves || 0;
    let employeeAllowedLeaves = parseFloat(personalLeaves);
    if (leaveTypes?.compassionate_leave) {
        employeeAllowedLeaves += parseFloat(leaveTypes?.compassionate_leave);
    }
    if(leaveTypes?.compensation_leave){
        employeeAllowedLeaves+=parseFloat(leaveTypes?.compensation_leave)
    }
    if (leaveTypes?.service_leave) {
        employeeAllowedLeaves += parseFloat(leaveTypes?.service_leave);
    }
    if (leaveTypes?.paternity_leaves) {
        employeeAllowedLeaves += parseFloat(leaveTypes?.paternity_leaves);
    }
    if (leaveTypes?.bereavement_leave) {
        employeeAllowedLeaves += parseFloat(leaveTypes?.bereavement_leave);
    }
    return parseFloat(employeeAllowedLeaves).toFixed(1);
}
export const badgeStyle = (color) => ({
  fontSize: "10px",
  color: "black",
  borderBottom: `solid 10px ${color}`,
  borderRadius: "0px",
});


export const leaveQuota = (leaveTypes, leaves, year, employee = null) => {
    const availed = AvailedLeavesCalculations(leaves);
    const employeeAllowedLeaves = parseFloat(allowedLeaves(leaveTypes));
    const hasLeft =
        employee?.leavingDate &&
        moment(employee.leavingDate).isValid();
    if (hasLeft) {
        return parseFloat(employeeAllowedLeaves - availed).toFixed(1);
    }
    const isCurrentYear = moment().format("YYYY") == year;
    const currentMonth = isCurrentYear ? moment().format("MM") : 12;
    const allowedPerMonth = employeeAllowedLeaves / 12;
    const allowedTillCurrentMonth = currentMonth * allowedPerMonth;
    return parseFloat(allowedTillCurrentMonth - availed).toFixed(1);
}

export const Options = [
    { value: "Excellent", label: "Excellent", score: 90 },
    { value: "Very Good", label: "Very Good", score: 85 },
    { value: "Good", label: "Good", score: 75 },
    { value: "Above Average", label: "Above Average", score: 65 },
    { value: "Average", label: "Average", score: 50 },
    { value: "Below Average", label: "Below Average", score: 40 },
    { value: "Fair", label: "Fair", score: 35 },
    { value: "Poor", label: "Poor", score: 25 },
    { value: "Very Poor", label: "Very Poor", score: 10 },

];


export const evaluationMonthFilter = [
    { label: "Last 1 Month", value: 2 },
    { label: "Last 2 Months", value: 3 },
    { label: "Last 6 Months", value: 6 },
    { label: "Last 9 Months", value: 9 },
    { label: "Last 1 Year", value: 12 },
    { label: "Last 2 Years", value: 24 },
];

export const evaluationsScores = {
    "Excellent": 90,
    "Very Good": 85,
    "Good": 75,
    "Above Average": 65,
    "Average": 50,
    "Below Average": 40,
    "Fair": 35,
    "Poor": 25,
    "Very Poor": 10,
    "Terrible": 5
};


export const LeaveCategory = (employeeLeaveType) => {
    const leavesOptions = [
        {
            label: 'Personal leave (Casual + Sick)',
            value: 'Personal leave (Casual + Sick)'
        },
        {
            label: 'Unpaid Leaves',
            value: 'Unpaid Leaves'
        },
        {
            label: 'Others',
            value: 'Others'
        }
    ];

    if (employeeLeaveType.compassionate_leave && parseFloat(employeeLeaveType.compassionate_leave) > 0) {
        leavesOptions.push({
            label: 'Compassionate Leave',
            value: 'Compassionate Leave'
        })
    }
    if (employeeLeaveType.compassionate_leave && parseFloat(employeeLeaveType.compassionate_leave) > 0) {
        leavesOptions.push({
        label: 'Public holiday compensation',
        value: 'Public holiday compensation'
    })
    }

    if (employeeLeaveType.service_leave && parseFloat(employeeLeaveType.service_leave) > 0) {
        leavesOptions.push({
            label: 'Long Service Leave',
            value: 'Long Service Leave'
        })
    }
    
    const unavailedCompLeaves = (employeeLeaveType.compensationLeavesDetails || []).filter(
    leave => leave && leave.status==="available"
    );
    if (unavailedCompLeaves.length > 0) {
        leavesOptions.push({
            label: 'Compensation Leave',
            value: 'Compensation Leave'
        })
    }

    if (employeeLeaveType.paternity_leaves && parseFloat(employeeLeaveType.paternity_leaves) > 0) {
        leavesOptions.push({
            label: 'Paternity Leave',
            value: 'Paternity Leave'
        })
    }

    if (employeeLeaveType.bereavement_leave && parseFloat(employeeLeaveType.bereavement_leave) > 0) {
        leavesOptions.push({
            label: 'Bereavement Leave',
            value: 'Bereavement Leave'
        })
    }

    return leavesOptions;
}

export const ScoreCalculation = (row, evaluationList) => {
    if (row.score >= 85) {
        evaluationList.great++
    }
    if (row.score < 85 && row.score >= 70) {
        evaluationList.good++
    }
    if (row.score >= 55 && row.score < 70) {
        evaluationList.avg++
    }
    if (row.score >= 35 && row.score < 55) {
        evaluationList.fair++
    }
    if (row.score < 35) {
        evaluationList.poor++
    }
    return evaluationList;
}

export   const breakTimeCalculation = (breakin, breakout) => {
    const breaks = [];
    // var breakTime=0;
    breakin.map((x, index) => {
      const breakTime =
        breakout[index] && x
          ? moment(breakout[index]).diff(moment(x), "seconds")
          : 0;

      breaks.push(
        <div>
          <CBadge color="info" style={{ margin: "2px" }}>
            {moment.utc(breakTime * 1000).format("HH:mm:ss")}
            {/* {parseInt(breakTime / 60) + ":" + (breakTime % 60)} */}
          </CBadge>
        </div>
      );
    });

    return breaks;
  };

export const hoursCalculations = (x, breakins, breakouts) => {
    const hours =
      x.checkout && x.checkin
        ? moment(x.checkout).diff(moment(x.checkin), "minutes")
        : 0;

    var breakTime = 0;
    breakins.map((x, index) => {
      breakTime +=
        breakouts[index] && x
          ? moment(breakouts[index]).diff(moment(x), "minutes")
          : 0;
    });

    const calculatedWorkingHours = hours - breakTime;

    const [calculatedHours, calculatedMinutes] = [parseInt(calculatedWorkingHours / 60), calculatedWorkingHours % 60];
    const [formattedHours, formattedMins] = [String(calculatedHours).padStart(2, '0'), String(calculatedMinutes).padStart(2, '0')]
    const formattedWorkingTime = `${formattedHours}:${formattedMins}`;
    return `${formattedWorkingTime}`;
  };

  export const isPublicHoliday = (publicLeaves,date) => {
    const formattedDate = moment(date).format("MM/DD/YYYY");
    return publicLeaves?.some((holiday) =>
      holiday.publicHolidays.includes(formattedDate)
    );
  };

  export const formatCNIC=(cnic)=> {
    // Remove any non-numeric characters from the input string
    const numericCNIC = cnic.replace(/\D/g, '');
  
    // Check if the numericCNIC is empty or doesn't match the expected length
    if (numericCNIC.length !== 13) {
      return 'Invalid CNIC';
    }
  
    // Format the CNIC as per the standard format
    const formattedCNIC = `${numericCNIC.slice(0, 5)}-${numericCNIC.slice(5, 12)}-${numericCNIC.slice(12)}`;
  
    return formattedCNIC;
  }

  export const  formatPhoneNumber=(phoneNumber) =>{
    // Remove any non-numeric characters from the input string
    const numericPhoneNumber = phoneNumber.replace(/\D/g, '');
  
    // Check if the numericPhoneNumber is empty or doesn't match the expected length
    if (numericPhoneNumber.length !== 11) {
      return 'Invalid phone number';
    }
  
    // Format the phone number as per the standard format
    const formattedPhoneNumber =
      numericPhoneNumber.slice(0, 4) + '-' + numericPhoneNumber.slice(4, 7) + '-' + numericPhoneNumber.slice(7);
  
    return formattedPhoneNumber;
  }
  
